import React from "react";
import { UseFormReturn } from "react-hook-form";

import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Stack, 
    Typography, 
} from "@mui/material";

import { DefaultTypedResponse } from "models/response";
import { Award, Festival } from "models";

import { t, Trans } from "@lingui/macro";

import {CreditsAndAwardsDataProps, CreditsAndAwardsDataState} from "./models";

import { ZPlayChipList, ZPlayFormField } from "commons/fields";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpIcon from '@mui/icons-material/Help';

import { CreateAndEditAwardModal } from "components/backoffice/award";
import { CreateAndEditFestivalModal } from "components/backoffice/festival";

import {ContentProducerService} from "services/content-producer-service";

import {withSnackbar, WithSnackbarProps} from "notistack";
import { ZPlayLoader } from "commons";


class CreditsAndAwardsData extends React.Component<CreditsAndAwardsDataProps & UseFormReturn & WithSnackbarProps, CreditsAndAwardsDataState> {

    constructor(props: any) {

        super(props);

        this.state = {
            openCreateFestivalModal: false,
            openCreateAwardModal: false,
            isLoading:false,
            awardList: this.props.awardList,
            festivalList: this.props.festivalList
        };
    }

    private openCreateFestivalModal = (): void => {
        this.setState(() => ({openCreateFestivalModal: true}));
    };

    private closeCreateFestivalModal = (): void => {
        this.setState(() => ({openCreateFestivalModal: false}));
    };

    private createFestivalCallback = (data: any) => {
        this.props.enqueueSnackbar(t({
            id: "df-success-create-festival",
            message: "Il festival è stato creato con successo"
        }), {
            variant: "success",
        });
        this.getFestivalsList();
        this.closeCreateFestivalModal();
    };

    private createFestivalErrorCallback = (err: any) => {
        this.props.enqueueSnackbar(t({
            id: "df-error-create-festival",
            message: "Si è verificato un errore nella creazione del festival"
        }), {
            variant: "error",
        });
        this.closeCreateFestivalModal();
    };

    private openCreateAwardModal = (): void => {
        this.setState(() => ({openCreateAwardModal: true}));
    };

    private closeCreateAwardModal = (): void => {
        this.setState(() => ({openCreateAwardModal: false}));
    };

    private createAwardCallback = (data: any) => {
        this.props.enqueueSnackbar(t({
            id: "df-success-create-award",
            message: "Il premio è stato creato con successo"
        }), {
            variant: "success",
        });
        this.getAwardsList();
        this.closeCreateAwardModal();
    };

    private createAwardErrorCallback = (err: any) => {
        this.props.enqueueSnackbar(t({
            id: "df-error-create-award",
            message: "Si è verificato un errore nella creazione del premio"
        }), {
            variant: "error",
        });
        this.closeCreateAwardModal();
    };


    private getFestivalsList = (): void => {
        this.setState(() => ({
            isLoading: true,
        }));
        ContentProducerService.getFestivalList(this.getFestivalCallback, this.getFestivalErrorCallback);
    };

    private getFestivalCallback = (data: DefaultTypedResponse<Festival>): void => {
        this.setState(() => ({
            festivalList: data.list,
            isLoading: false
        }));
    };

    private getFestivalErrorCallback = (err: any): void => {
        this.props.enqueueSnackbar(t({
            id: "df-error-get-festival",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati"
        }), {
            variant: "error",
        });
        this.setState(() => ({
            isLoading: false,
        }));
    };


    private getAwardsList = (): void => {
        this.setState(() => ({
            isLoading: true,
        }));
        ContentProducerService.getAwardList(this.getAwardCallback, this.getAwardErrorCallback);
    };

    private getAwardCallback = (data: DefaultTypedResponse<Award>): void => {
        this.setState(() => ({
            awardList: data.list,
            isLoading: false
        }));
    };

    private getAwardErrorCallback = (err: any): void => {
        this.props.enqueueSnackbar(t({
            id: "df-error-get-award",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati"
        }), {
            variant: "error",
        });
        this.setState(() => ({
            isLoading: false,
        }));
    };

  render(){
    return(<>
      <Accordion sx={{backgroundColor: process.env.REACT_APP_THIRD_COLOR}}>
        <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{color: "white"}}/>}
        aria-controls="panel1a-content"
        id="panel1a-header"
        >
            <Typography color={process.env.REACT_APP_DEFAULT_TEXT_COLOR}>
                <Trans id={"df-credits-section"}>Crediti e riconoscimenti del video</Trans>
            </Typography>
        </AccordionSummary>
        <AccordionDetails>
            <ZPlayFormField
                label={t({
                    id: "df-director",
                    message: "Regista"
                })}
                name={`${this.props.name}.director`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-director-explanation"}> Se il regista è più di uno, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-producer",
                    message: "Produttore"
                })}
                name={`${this.props.name}.producer`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-producer-explanation"}> Se i produttori sono più di uno, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-cast",
                    message: "Cast"
                })}
                name={`${this.props.name}.cast`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-cast-explanation"}> Inserisci i nomi degli attori separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-writer",
                    message: "Writer"
                })}
                name={`${this.props.name}.writer`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-writer-explanation"}> Se lo sceneggiatore è più di uno, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-composer",
                    message: "Composer"
                })}
                name={`${this.props.name}.composer`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-composer-explanation"}> Se il compositore è più di uno, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-dop",
                    message: "Direttore della fotografia"
                })}
                name={`${this.props.name}.dop`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-dop-explanation"}> Se ci sono più direttori della fotografia, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-editor",
                    message: "Editor"
                })}
                name={`${this.props.name}.editor`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-editor-explanation"}> Se l'editore è più di uno, inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>
            <ZPlayFormField
                label={t({
                    id: "df-academy-qualified",
                    message: "Academy qualified"
                })}
                name={`${this.props.name}.academyQualified`}
                control={this.props.control}
                formState={this.props.formState}
                trigger={this.props.trigger}
            />
            <Stack direction="row" spacing={1} mt={1} mb={2}>
                <HelpIcon fontSize="small" sx={{color: process.env.REACT_APP_SECONDARY_TEXT_COLOR}}/>
                <Typography
                    variant={"body2"}
                    color={process.env.REACT_APP_SECONDARY_TEXT_COLOR}>
                        <Trans id={"df-academy-qualified-explanation"}> Inserisci i nomi separati da virgola </Trans>
                </Typography>
            </Stack>

            {!this.state.isLoading && <ZPlayChipList
                chipsList={this.state.festivalList}
                selectedChipsIds={this.props.selectedFestivals? this.props.selectedFestivals : []}
                color={process.env.REACT_APP_BACKOFFICE_CHIP_COLOR_FESTIVAL}
                updateListCallback={this.props.updateContentFestivals}
                createChipCallback={this.openCreateFestivalModal}
                title={"Festival"}
                titleId={"df-festivals-label"}
                textId={"zpcl-festival-explanation"}
                text={"Scegli e seleziona i festival sottostanti per aggiungerli alla lista dei festival del tuo contenuto. Puoi inoltre creare dei festival personalizzati schiacciando sull'apposito bottone."}
            />}

            {!this.state.isLoading && <ZPlayChipList
                chipsList={this.state.awardList}
                selectedChipsIds={this.props.selectedAwards? this.props.selectedAwards : []}
                color={process.env.REACT_APP_BACKOFFICE_CHIP_COLOR_AWARD}
                updateListCallback={this.props.updateContentAwards}
                createChipCallback={this.openCreateAwardModal}
                title={"Premi"}
                titleId={"df-awards-label"}
                textId={"zpcl-award-explanation"}
                text={"Scegli e seleziona i premi sottostanti per aggiungerli alla lista dei premi del tuo contenuto. Puoi inoltre creare dei premi personalizzati schiacciando sull'apposito bottone."}

            />}
        </AccordionDetails>
    </Accordion>
        {this.state.openCreateFestivalModal &&
            <CreateAndEditFestivalModal
                createFestivalCallback={this.createFestivalCallback}
                createFestivalErrorCallback={this.createFestivalErrorCallback}
                closeModal={this.closeCreateFestivalModal} />}

        {this.state.openCreateAwardModal &&
        <CreateAndEditAwardModal
          createAwardCallback={this.createAwardCallback}
          createAwardErrorCallback={this.createAwardErrorCallback}
          closeModal={this.closeCreateAwardModal}/>}
        {this.state.isLoading && <ZPlayLoader />}
    </>)
  }
}

export default withSnackbar(CreditsAndAwardsData);